import type { CustomConsoleCommand, CustomConsoleCursor, CustomConsoleEvent } from '../types/custom';

export type ConsoleConnectionState = Extract<CustomConsoleEvent, { type: 'state' }>['state'];
export type ConsoleCommandStatus = 'pending' | 'completed' | 'not_started' | 'failed' | 'unknown' | 'error';

export type ConsoleEntry =
  | { kind: 'command'; id: number; sessionId: string; requestId: string; line: string; status: ConsoleCommandStatus; lines: string[]; code?: string }
  | { kind: 'log' | 'notice' | 'error'; id: number; message: string; code?: string };

export interface CustomConsoleState {
  sessionId: string | null;
  connection: ConsoleConnectionState;
  message: string;
  commands: CustomConsoleCommand[];
  entries: ConsoleEntry[];
  nextId: number;
  consumed?: { sessionId: string; cursor: CustomConsoleCursor; version: number };
}

export type CustomConsoleAction =
  | { type: 'open'; sessionId: string }
  | { type: 'disconnect'; message: string }
  | { type: 'command'; sessionId: string; requestId: string; line: string }
  | { type: 'event'; sessionId: string; event: CustomConsoleEvent }
  | { type: 'clear' };

export const CONSOLE_HISTORY_LIMIT = 100;
export const CONSOLE_PENDING_LIMIT = 16;
export const CONSOLE_ENTRY_LIMIT = 2_000;
const TRANSCRIPT_CHARACTER_LIMIT = 2 * 1024 * 1024;
const UNKNOWN_RESULT = 'Result unknown. The command may have executed; it was not sent again.';

export const initialCustomConsoleState: CustomConsoleState = {
  sessionId: null,
  connection: 'disconnected',
  message: '',
  commands: [],
  entries: [],
  nextId: 1,
};

/** Literal control characters never act as terminal instructions. */
export function consoleLiteralText(text: string): string {
  // This intentionally detects controls to display them as inert, visible text.
  // eslint-disable-next-line no-control-regex
  return text.replace(/[\u0000-\u0008\u000b-\u001f\u007f-\u009f]/g, character =>
    `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`);
}

export function consoleCommandBytes(line: string): number {
  return new TextEncoder().encode(line).byteLength;
}

/** Physical text rows preserve blank lines and give wrapped continuations the same indent. */
export function consoleDisplayRows(lines: string[]): Array<{ text: string; indent: number }> {
  return lines.flatMap(line => line.split('\n').map(text => ({ text, indent: text.match(/^ */)?.[0].length || 0 })));
}

export interface ConsoleCommandSuggestion {
  name: string;
  hasChildren: boolean;
  command?: CustomConsoleCommand;
}

function isCommandCompletionPosition(line: string, start: number | null, end: number | null): boolean {
  return line.length > 0 && !/\s/.test(line) && start === line.length && end === line.length;
}

/** Complete one dot segment at a time, using only names supplied by this session. */
export function consoleCommandSuggestions(commands: CustomConsoleCommand[], line: string,
  start: number | null = line.length, end: number | null = start): ConsoleCommandSuggestion[] {
  if (!isCommandCompletionPosition(line, start, end)) return [];
  const parentEnd = line.lastIndexOf('.') + 1;
  const suggestions = new Map<string, ConsoleCommandSuggestion>();
  for (const command of commands) {
    // A catalog may describe names that cannot be safely inserted as one command token.
    if (/\s/.test(command.name) || command.name.split('.').some(segment => !segment)
      || consoleLiteralText(command.name) !== command.name || !command.name.startsWith(line)) continue;
    const childEnd = command.name.indexOf('.', parentEnd);
    const name = childEnd < 0 ? command.name : command.name.slice(0, childEnd);
    if (name === line) continue;
    const suggestion: ConsoleCommandSuggestion = suggestions.get(name) || { name, hasChildren: false };
    if (childEnd < 0) suggestion.command = command;
    else suggestion.hasChildren = true;
    suggestions.set(name, suggestion);
  }
  return [...suggestions.values()];
}

/** Unsupported caret/argument positions are left untouched; accepting never submits. */
export function completeConsoleCommand(line: string, name: string, start: number | null, end: number | null): string | null {
  if (!isCommandCompletionPosition(line, start, end) || name === line || !name.startsWith(line)) return null;
  return name;
}

function unknownPending(entries: ConsoleEntry[]): ConsoleEntry[] {
  return entries.map(entry => entry.kind === 'command' && entry.status === 'pending'
    ? { ...entry, status: 'unknown', lines: [UNKNOWN_RESULT] }
    : entry);
}

function entrySize(entry: ConsoleEntry): number {
  return entry.kind === 'command' ? entry.line.length + entry.lines.reduce((size, line) => size + line.length, 0) : entry.message.length;
}

function bounded(entries: ConsoleEntry[]): ConsoleEntry[] {
  let size = entries.reduce((total, entry) => total + entrySize(entry), 0);
  let count = entries.length;
  // Evict entire old entries, preserving pending commands and the newest entry intact.
  return entries.filter((entry, index) => {
    if ((count > CONSOLE_ENTRY_LIMIT || size > TRANSCRIPT_CHARACTER_LIMIT)
      && index < entries.length - 1 && !(entry.kind === 'command' && entry.status === 'pending')) {
      size -= entrySize(entry);
      count -= 1;
      return false;
    }
    return true;
  });
}

export function customConsoleReducer(state: CustomConsoleState, action: CustomConsoleAction): CustomConsoleState {
  if (action.type === 'clear') return { ...state, entries: state.entries.filter(entry => entry.kind === 'command' && entry.status === 'pending') };
  if (action.type === 'disconnect') return {
    ...state, sessionId: null, connection: 'disconnected', message: action.message,
    entries: unknownPending(state.entries), consumed: undefined, commands: [],
  };
  if (action.type === 'open') return {
    ...state, sessionId: action.sessionId, connection: 'connecting', message: '',
    entries: unknownPending(state.entries), consumed: undefined, commands: [],
  };
  if (action.sessionId !== state.sessionId) return state;
  if (action.type === 'command') return {
    ...state, nextId: state.nextId + 1,
    entries: bounded([...state.entries, {
      kind: 'command', id: state.nextId, sessionId: action.sessionId,
      requestId: action.requestId, line: consoleLiteralText(action.line), status: 'pending', lines: [],
    }]),
  };

  const event = action.event;
  if (event.type === 'state') return {
    ...state, connection: event.state, message: event.message || '',
    commands: event.state === 'connected' ? state.commands : [],
    entries: event.state === 'disconnected' || event.state === 'error' ? unknownPending(state.entries) : state.entries,
  };
  if (event.type === 'commands') return state.connection === 'connected' ? { ...state, commands: event.commands } : state;
  if (event.type === 'result' || (event.type === 'error' && event.requestId)) {
    const index = state.entries.findIndex(entry => entry.kind === 'command'
      && entry.sessionId === action.sessionId && entry.requestId === event.requestId);
    if (index === -1) return state;
    const entry = state.entries[index];
    if (entry.kind !== 'command' || (entry.status !== 'pending' && entry.status !== 'unknown')) return state;
    const updated: ConsoleEntry = event.type === 'result'
      ? { ...entry, status: 'completed', lines: event.lines.map(consoleLiteralText) }
      : { ...entry, status: event.execution || 'error', lines: [consoleLiteralText(event.message)], code: event.code };
    return { ...state, entries: bounded(state.entries.map((previous, current) => current === index ? updated : previous)) };
  }
  const messages = event.type === 'logs' ? event.messages : [event.message];
  const cursor = event.type === 'logs' || event.type === 'notice' ? event.cursor : undefined;
  return {
    ...state,
    nextId: state.nextId + messages.length,
    entries: bounded([...state.entries, ...messages.map((message, index): ConsoleEntry => ({
      kind: event.type === 'logs' ? 'log' : event.type,
      id: state.nextId + index, message: consoleLiteralText(message),
      ...(event.type === 'error' ? { code: event.code } : {}),
    }))]),
    ...(cursor ? { consumed: { sessionId: action.sessionId, cursor, version: (state.consumed?.version || 0) + 1 } } : {}),
  };
}

function isCursor(value: unknown): value is CustomConsoleCursor {
  if (!value || typeof value !== 'object') return false;
  const cursor = value as Record<string, unknown>;
  return typeof cursor.streamId === 'string' && cursor.streamId.length > 0
    && typeof cursor.afterSeq === 'string' && /^\d+$/.test(cursor.afterSeq);
}

export function isCustomConsoleEvent(value: unknown): value is CustomConsoleEvent {
  if (!value || typeof value !== 'object') return false;
  const event = value as Record<string, unknown>;
  switch (event.type) {
    case 'commands': return Array.isArray(event.commands) && event.commands.length <= 1024
      && event.commands.every(command => command && typeof command === 'object'
        && ['name', 'usage', 'description'].every(key => typeof command[key] === 'string' && command[key].length > 0))
      && consoleCommandBytes(JSON.stringify(event.commands)) <= 256 * 1024;
    case 'state': return typeof event.state === 'string' && ['disconnected', 'connecting', 'authenticating', 'connected', 'error'].includes(event.state)
      && (event.message === undefined || typeof event.message === 'string');
    case 'result': return typeof event.requestId === 'string' && event.execution === 'completed'
      && Array.isArray(event.lines) && event.lines.every(line => typeof line === 'string');
    case 'error': return typeof event.message === 'string' && typeof event.code === 'string'
      && (event.requestId === undefined || typeof event.requestId === 'string')
      && (event.execution === undefined || ['not_started', 'failed', 'unknown'].includes(String(event.execution)));
    case 'logs': return Array.isArray(event.messages) && event.messages.every(message => typeof message === 'string') && isCursor(event.cursor);
    case 'notice': return typeof event.message === 'string' && (event.cursor === undefined || isCursor(event.cursor));
    default: return false;
  }
}

export function consoleTranscriptText(entries: ConsoleEntry[]): string {
  return entries.map(entry => entry.kind === 'command'
    ? [`> ${entry.line}`, `[${entry.status}]`, ...entry.lines].join('\n') : entry.message).join('\n');
}
